/**
 * useDragPersist — small "make this thing draggable and remember where
 * the user put it for the session" hook.
 *
 * - Position is stored in **sessionStorage** so it survives navigation
 *   but resets on browser close (matches the product brief: "for the
 *   rest of the session").
 * - Uses pointer events → mouse + touch + stylus in one shot.
 * - Clamps to the viewport so the widget can never end up off-screen.
 * - Returns `dragged=false` until the user actually moves it — the
 *   caller can render a nicer "rail tab" chrome when the launcher is
 *   still at its default anchor.
 */
import * as React from "react";

interface DragState {
  x: number;
  y: number;
  dragged: boolean;
}

interface Options {
  storageKey: string;
  /** Element footprint used for viewport clamping. */
  width: number;
  height: number;
  /** Padding from viewport edges when clamping. */
  padding?: number;
}

function readSaved(key: string): DragState | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x !== "number" || typeof parsed?.y !== "number") return null;
    return { x: parsed.x, y: parsed.y, dragged: true };
  } catch {
    return null;
  }
}

export function useDragPersist({ storageKey, width, height, padding = 8 }: Options) {
  const [pos, setPos] = React.useState<DragState>(() => readSaved(storageKey) ?? { x: 0, y: 0, dragged: false });
  const draggingRef = React.useRef<{ startX: number; startY: number; posX: number; posY: number } | null>(null);
  const movedRef = React.useRef(false);

  const clamp = React.useCallback((x: number, y: number) => {
    if (typeof window === "undefined") return { x, y };
    const maxX = Math.max(padding, window.innerWidth - width - padding);
    const maxY = Math.max(padding, window.innerHeight - height - padding);
    return {
      x: Math.max(padding, Math.min(x, maxX)),
      y: Math.max(padding, Math.min(y, maxY)),
    };
  }, [width, height, padding]);

  // Keep the position clamped when the viewport resizes so a widget
  // dragged into the corner of a big screen doesn't disappear off the
  // edge on a small one.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => {
      setPos((prev) => {
        if (!prev.dragged) return prev;
        const c = clamp(prev.x, prev.y);
        return { ...prev, ...c };
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clamp]);

  const handlePointerDown = React.useCallback((e: React.PointerEvent) => {
    // Ignore right-clicks / scroll gestures.
    if (e.button !== 0 && e.pointerType === "mouse") return;
    // Ignore drags starting from interactive children — buttons, inputs,
    // links inside the drag handle should still behave normally.
    const target = e.target as HTMLElement;
    if (target.closest("button,input,textarea,select,a,[data-no-drag]") && target !== e.currentTarget) return;

    // Capture pointer so we get every subsequent move even outside the
    // element (essential for smooth dragging near the viewport edge).
    try { (e.currentTarget as Element).setPointerCapture(e.pointerId); } catch { /* ignore */ }
    movedRef.current = false;

    // Resolve the current on-screen origin so the drag starts from where
    // the user's finger actually is, regardless of whether we were on
    // the default anchor or a previous saved position.
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    draggingRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      posX: rect.left,
      posY: rect.top,
    };

    const onMove = (ev: PointerEvent) => {
      const st = draggingRef.current;
      if (!st) return;
      const dx = ev.clientX - st.startX;
      const dy = ev.clientY - st.startY;
      if (!movedRef.current && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
        movedRef.current = true;
      }
      const next = clamp(st.posX + dx, st.posY + dy);
      setPos({ x: next.x, y: next.y, dragged: true });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      const st = draggingRef.current;
      draggingRef.current = null;
      if (!movedRef.current || !st) return;
      // Only persist when there was an actual drag — a plain click on
      // the drag handle shouldn't overwrite the default anchor.
      setPos((prev) => {
        try {
          sessionStorage.setItem(storageKey, JSON.stringify({ x: prev.x, y: prev.y }));
        } catch { /* quota / private mode */ }
        return prev;
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }, [clamp, storageKey]);

  const reset = React.useCallback(() => {
    try { sessionStorage.removeItem(storageKey); } catch { /* ignore */ }
    setPos({ x: 0, y: 0, dragged: false });
  }, [storageKey]);

  return {
    pos,                 // { x, y, dragged }
    draggableProps: {
      onPointerDown: handlePointerDown,
      style: { touchAction: "none" as const },  // stop the browser scrolling while dragging
    },
    /** Whether the user actually clicked-and-dragged (vs just tapped). */
    wasClick: !movedRef.current,
    reset,
  };
}
